import { createContext, useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, X, ArrowRight, Play } from "lucide-react";
import PlanDeMatchModal from "@/components/PlanDeMatchModal";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SkipToContent from "@/components/SkipToContent";
import LazyYouTubeEmbed from "@/components/LazyYouTubeEmbed";
import { Button } from "@/components/ui/button";
import { initCalEmbed, booking15MinProps } from "@/hooks/use-cal";
import PlanDeMatchCalendar from "@/components/PlanDeMatchCalendar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";



const Accent = ({ children }: { children: React.ReactNode }) => (
  <span
    style={{
      fontFamily: "'Playfair Display', serif",
      fontStyle: "italic",
      fontWeight: 400,
      fontSize: "1.12em",
      lineHeight: 1,
      color: "hsl(var(--primary))",
    }}
  >
    {children}
  </span>
);

const monoStyle = { fontFamily: "'JetBrains Mono', 'Inter', monospace" } as const;

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <span
    style={monoStyle}
    className="inline-flex items-center gap-2 text-[11px] md:text-xs tracking-[0.22em] uppercase text-primary/90"
  >
    <span className="h-1 w-1 rounded-full bg-primary" />
    {children}
  </span>
);

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

const CTAContext = createContext<() => void>(() => {});

const CTAGroup = ({ align = "center" }: { align?: "center" | "start" }) => {
  const openModal = useContext(CTAContext);
  return (
    <div
      className={`flex w-full flex-col ${
        align === "center" ? "items-center text-center" : "items-start text-left"
      }`}
    >
      <Button
        size="lg"
        onClick={openModal}
        className="group w-full rounded-full bg-primary px-8 py-6 text-base font-semibold text-primary-foreground transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary/90 sm:w-auto"
        style={{ boxShadow: "0 18px 45px -18px hsl(var(--primary) / 0.65)" }}
      >
        Réserver mon Plan de match · 500 $
        <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </Button>

      <Button
        variant="outline"
        className="mt-2.5 w-full rounded-full border border-foreground/25 bg-transparent px-6 py-4 text-sm font-medium text-foreground hover:bg-foreground/[0.06] sm:w-auto"
        {...booking15MinProps}
      >
        J'ai des questions? Jasons 15 minutes
      </Button>

      <p className="mt-3 text-xs text-muted-foreground">
        Sans engagement. Zéro pitch de vente.
      </p>
    </div>
  );
};

const microPreuves = [
  "Document complet livré en 7 jours",
  "Satisfait ou 500 $ remboursé",
];

const problemes = [
  {
    title: "Contenu sans conversion",
    body: "Vous publiez régulièrement, vous avez des likes et des vues, mais cela ne se transforme pas en clients. Votre contenu attire des curieux, pas des acheteurs.",
  },
  {
    title: "Publicité qui brûle votre budget",
    body: "Vous investissez en pub, mais les leads sont non qualifiés, le coût par acquisition monte, et vous ne savez pas ce qui fonctionne vraiment.",
  },
  {
    title: "Ou vous ne savez pas par où commencer",
    body: "Vous n'avez pas le temps, pas l'équipe, et chaque expert dit le contraire de l'autre. Résultat : vous ne faites rien, ou vous copiez ce que les autres font.",
  },
];

const questions = [
  {
    title: "Où investir",
    body: "Publicité ou pas, combien, sur quelles plateformes, avec quels messages. Et où arrêter de perdre de l'argent.",
  },
  {
    title: "Dans quel ordre",
    body: "Quoi faire en premier, en deuxième, en troisième. Une séquence claire, pas une liste infinie de bonnes idées.",
  },
];

const inclusions = [
  "Appel de départ pour comprendre votre business et vos enjeux",
  "Analyse de votre positionnement, votre contenu et vos publicités",
  "Analyse de votre funnel complet : de la première vue jusqu'à la vente",
  "Document écrit avec recommandations détaillées",
  "Rencontre de présentation d'environ 1 heure",
];

const pourToi = [
  "Vous offrez des services B2B, du courtage ou du coaching",
  "Vous voulez savoir exactement quoi faire, pas des théories",
  "Vous êtes prêt à exécuter un plan clair",
  "Vous cherchez une approche structurée et mesurable",
];

const pasPourToi = [
  "Vous cherchez juste à avoir plus de followers",
  "Vous voulez des résultats sans rien changer",
  "Vous préférez continuer à improviser",
  "Vous cherchez une solution miracle",
];

const processus = [
  {
    num: "01",
    title: "On apprend votre business",
    body: "Un appel pour comprendre exactement comment votre business fonctionne et quels sont vos enjeux marketing. Vous allez avoir des devoirs avant et après la rencontre : nous envoyer vos documents, vos dépenses pub, vos chiffres.",
  },
  {
    num: "02",
    title: "On analyse tout",
    body: "Une analyse stratégique complète pour identifier exactement ce qui fonctionne, ce qui ne fonctionne pas, et ce qui peut être amélioré.",
  },
  {
    num: "03",
    title: "On vous présente votre plan",
    body: "Vous recevez le document complet, et on se fait une rencontre d'environ une heure pour tout passer au travers, étape par étape.",
  },
];

const faqs = [
  {
    q: "Qu'est-ce que je reçois exactement pour 500 $?",
    a: "Un appel de départ, une analyse complète de votre positionnement, de votre contenu, de vos publicités et de votre funnel, un document écrit avec recommandations détaillées, et une rencontre d'environ une heure pour tout vous présenter.",
  },
  {
    q: "Combien de temps ça prend?",
    a: "Le Plan de match est livré en 7 jours ouvrables après l'appel de départ.",
  },
  {
    q: "Est-ce que vous allez essayer de me vendre quelque chose?",
    a: "Non. Le Plan de match, c'est le produit. La rencontre de présentation sert à passer au travers de votre plan, pas à vous pitcher. Le document vous appartient et vous l'exécutez comme vous voulez.",
  },
  {
    q: "Et si je trouve que ça ne vaut pas 500 $?",
    a: "On vous rembourse. Si vous passez au travers de votre Plan de match et que vous trouvez que vous n'avez pas eu votre valeur, vous nous le dites et on vous remet votre 500 $.",
  },
  {
    q: "C'est pour quel type de business?",
    a: "Les entrepreneurs de services : B2B, courtage, coaching, consultation. Si vos clients doivent vous faire confiance avant d'acheter, le Plan de match s'applique à vous.",
  },
];

const MobileStickyCTA = () => {
  const openModal = useContext(CTAContext);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > window.innerHeight * 0.85);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background px-4 py-3 transition-opacity duration-300 md:hidden ${
        visible ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
      style={{ maxHeight: 72 }}
    >
      <Button
        onClick={openModal}
        className="h-12 w-full rounded-full bg-primary text-base font-semibold text-primary-foreground hover:bg-primary/90"
      >
        Réserver · 500 $
      </Button>
    </div>
  );
};

const PlanDeMatchPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  useEffect(() => {
    initCalEmbed();
  }, []);
  return (
    <CTAContext.Provider value={() => setModalOpen(true)}>
    <div className="pdm-scope min-h-screen bg-background text-foreground overflow-x-hidden">
      <PlanDeMatchModal open={modalOpen} onOpenChange={setModalOpen} />
      <SEOHead
        title="Plan de match 8020 | Votre plan marketing complet en 7 jours"
        description="On analyse votre business au complet et on vous remet le plan exact de ce que vous devez faire : quoi prioriser, où investir, dans quel ordre. 500 $, livré en 7 jours."
        canonical="https://agence8020.com/plan-de-match"
      />
      <SkipToContent />
      <Navbar />

      <main id="main-content">
        {/* SECTION 1 — HERO */}
        <section className="relative pt-28 pb-20 md:pt-40 md:pb-32">
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 70% 55% at 50% 0%, hsl(var(--primary) / 0.13) 0%, transparent 70%)",
            }}
          />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.35]"
            style={{
              backgroundImage:
                "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)",
              backgroundSize: "64px 64px",
              maskImage:
                "radial-gradient(ellipse 60% 50% at 50% 20%, black, transparent 75%)",
              WebkitMaskImage:
                "radial-gradient(ellipse 60% 50% at 50% 20%, black, transparent 75%)",
            }}
          />

          <div className="container relative mx-auto max-w-5xl px-5 md:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span
                style={monoStyle}
                className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/[0.07] px-4 py-2 text-[10px] md:text-[11px] tracking-[0.2em] uppercase text-primary"
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-70" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
                </span>
                PLACES LIMITÉES
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-7 text-[2.1rem] leading-[1.08] sm:text-5xl md:text-6xl lg:text-[4.4rem] font-semibold tracking-[-0.03em]"
            >
              Votre plan de match pour un système qui vous amène des{" "}
              <Accent>clients.</Accent>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mx-auto mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-muted-foreground"
            >
              On analyse votre business au complet et on vous remet le plan exact de ce que
              vous devez faire : quoi prioriser, où investir, dans quel ordre. Vous repartez
              avec la clarté.
            </motion.p>

            {/* Bloc vidéo VIDEO_VSL */}
            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative mx-auto mt-12 w-full max-w-3xl"
            >
              <div
                className="pointer-events-none absolute -inset-6 rounded-[2rem] opacity-60 blur-2xl"
                style={{
                  background:
                    "radial-gradient(ellipse at center, hsl(var(--primary) / 0.22), transparent 70%)",
                }}
              />
              <LazyYouTubeEmbed
                videoId="kmbXNMOc2IY"
                title="Plan de match, Agence 8020"
                autoplay
                muted
                loop={false}
                showCaptions
                alwaysShowSound
                className="relative overflow-hidden rounded-2xl border border-border bg-card md:rounded-3xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="mt-10 flex flex-col items-center"
            >
              <CTAGroup />

              <ul className="mt-7 flex flex-col items-center gap-3 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-7">
                {microPreuves.map((m) => (
                  <li key={m} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 shrink-0 text-primary" />
                    {m}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2 — PONT */}
        <section className="relative py-20 md:py-28">
          <div className="container mx-auto max-w-4xl px-5 md:px-8">
            <motion.figure
              {...reveal}
              transition={{ duration: 0.7 }}
              className="relative overflow-hidden rounded-3xl border border-border bg-card px-6 py-12 md:px-14 md:py-16 text-center"
            >
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "radial-gradient(ellipse 80% 60% at 50% 0%, hsl(var(--primary) / 0.08), transparent 70%)",
                }}
              />
              <span
                aria-hidden
                className="relative block text-6xl md:text-8xl leading-none text-primary/25"
                style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}
              >
                “
              </span>
              <blockquote className="relative -mt-6 md:-mt-8 text-xl sm:text-2xl md:text-3xl lg:text-4xl leading-[1.35] font-medium tracking-[-0.02em]">
                « Un système qui vous amène des clients, ça commence pas par travailler plus
                fort. Ça commence par savoir exactement où vous en êtes. »
              </blockquote>
              <figcaption className="relative mt-8 text-sm md:text-base text-muted-foreground">
                Chaque business est différente. Voici comment on trouve le vôtre.
              </figcaption>
            </motion.figure>
          </div>
        </section>

        {/* SECTION 3 — LE PROBLÈME */}
        <section className="py-20 md:py-28">
          <div className="container mx-auto max-w-6xl px-5 md:px-8">
            <motion.div {...reveal} transition={{ duration: 0.6 }} className="max-w-2xl">
              <Eyebrow>LE PROBLÈME</Eyebrow>
              <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-semibold tracking-[-0.03em]">
                Vous travaillez fort, mais vous <Accent>devinez.</Accent>
              </h2>
            </motion.div>

            <div className="mt-12 grid gap-5 md:mt-16 md:grid-cols-3">
              {problemes.map((p, i) => (
                <motion.article
                  key={p.title}
                  {...reveal}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="group relative overflow-hidden rounded-2xl border border-border bg-card p-7 md:p-8 transition-colors duration-300 hover:border-primary/30"
                >
                  <div
                    className="pointer-events-none absolute inset-x-0 top-0 h-px opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, hsl(var(--primary) / 0.7), transparent)",
                    }}
                  />
                  <span style={monoStyle} className="text-xs text-primary/60">
                    0{i + 1}
                  </span>
                  <h3 className="mt-4 text-lg md:text-xl font-semibold">{p.title}</h3>
                  <p className="mt-3 text-sm md:text-base leading-relaxed text-muted-foreground">
                    {p.body}
                  </p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 4 — LE RECADRAGE */}
        <section className="relative py-20 md:py-28">
          <div className="container mx-auto max-w-6xl px-5 md:px-8">
            <div className="grid gap-10 lg:grid-cols-[1fr_1.05fr] lg:gap-16 lg:items-start">
              <motion.div {...reveal} transition={{ duration: 0.6 }} className="lg:sticky lg:top-28">
                <Eyebrow>LE VRAI ENJEU</Eyebrow>
                <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-semibold tracking-[-0.03em]">
                  Le problème, ce n'est pas votre effort. C'est le <Accent>flou.</Accent>
                </h2>
                <p className="mt-6 text-sm md:text-base leading-relaxed text-muted-foreground">
                  Chaque business est différente. Ce qui fonctionne pour un courtier
                  hypothécaire ne fonctionne pas pour un coach. Ce qui fonctionne pour votre
                  compétiteur ne fonctionnera peut-être pas pour vous. Copier des tactiques donne
                  des résultats aléatoires. Vous n'avez pas besoin de plus de tactiques. Vous avez
                  besoin de votre plan.
                </p>
              </motion.div>

              <div className="space-y-4">
                {questions.map((q, i) => (
                  <motion.div
                    key={q.title}
                    {...reveal}
                    transition={{ duration: 0.6, delay: i * 0.1 }}
                    className="group flex gap-5 rounded-2xl border border-border bg-card p-6 md:p-7 transition-all duration-300 hover:border-primary/30 hover:-translate-y-0.5"
                  >
                    <span
                      style={monoStyle}
                      className="mt-1 shrink-0 text-xl md:text-2xl text-primary"
                    >
                      ?
                    </span>
                    <div>
                      <h3 className="text-lg md:text-xl font-semibold">{q.title}</h3>
                      <p className="mt-2 text-sm md:text-base leading-relaxed text-muted-foreground">
                        {q.body}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5 — LE PLAN DE MATCH */}
        <section className="relative py-20 md:py-32">
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 60% 45% at 50% 50%, hsl(var(--primary) / 0.09), transparent 70%)",
            }}
          />
          <div className="container relative mx-auto max-w-4xl px-5 md:px-8">
            <motion.div {...reveal} transition={{ duration: 0.6 }} className="text-center">
              <Eyebrow>L'OFFRE</Eyebrow>
              <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-semibold tracking-[-0.03em]">
                Le Plan de match 8020. <Accent>Complet.</Accent>
              </h2>
              <p className="mx-auto mt-6 max-w-2xl text-sm md:text-base leading-relaxed text-muted-foreground">
                On analyse votre business au complet, et on vous remet exactement ce que vous
                devez faire. Le document vous appartient. Vous l'exécutez vous-même, avec votre
                équipe, avec qui vous voulez.
              </p>
            </motion.div>

            <motion.div
              {...reveal}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="relative mt-12 md:mt-16"
            >
              <div
                className="pointer-events-none absolute -inset-[1px] rounded-[1.75rem]"
                style={{
                  background:
                    "linear-gradient(160deg, hsl(var(--primary) / 0.55), transparent 45%, hsl(var(--primary) / 0.2))",
                }}
              />
              <div
                className="relative overflow-hidden rounded-[1.7rem] bg-card p-7 md:p-12"
                style={{ boxShadow: "0 50px 100px -40px hsl(var(--primary) / 0.28)" }}
              >
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "radial-gradient(ellipse 70% 50% at 50% 0%, hsl(var(--primary) / 0.08), transparent 70%)",
                  }}
                />
                <div className="relative">
                  <h3 className="text-2xl md:text-3xl font-semibold tracking-[-0.02em]">
                    Le Plan de match 8020
                  </h3>
                  <p className="mt-3 text-sm md:text-base text-muted-foreground">
                    Votre analyse complète et votre plan d'action, livrés en 7 jours ouvrables
                  </p>

                  <div className="mt-8 flex items-end gap-2">
                    <span
                      className="text-6xl md:text-8xl font-semibold leading-none tracking-[-0.05em] text-primary"
                      style={monoStyle}
                    >
                      500 $
                    </span>
                    <span className="pb-2 text-xs md:text-sm text-muted-foreground">+tx</span>
                  </div>

                  <div className="mt-9 h-px w-full bg-border" />

                  <ul className="mt-8 grid gap-4 sm:grid-cols-2">
                    {inclusions.map((inc) => (
                      <li key={inc} className="flex gap-3">
                        <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/12">
                          <Check className="h-3 w-3 text-primary" />
                        </span>
                        <span className="text-sm md:text-[0.95rem] leading-relaxed text-muted-foreground">
                          {inc}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-10">
                    <CTAGroup align="start" />
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.aside
              {...reveal}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-6 rounded-2xl border border-border bg-secondary/40 p-7 md:p-9"
            >
              <h3 className="text-lg md:text-xl font-semibold">
                Zéro pitch. Et une garantie.
              </h3>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-muted-foreground">
                On est tannés des appels découverte où la personne veut juste vous vendre
                son produit. Nous, on fait l'inverse : on vous donne de la valeur, et
                après, vous repartez et vous faites ce que vous voulez avec. Et si vous trouvez que
                votre Plan de match ne vaut pas votre 500 $, on vous le rembourse. Simple de même.
              </p>
            </motion.aside>
          </div>
        </section>

        {/* SECTION 6 — POUR TOI / PAS POUR TOI */}
        <section className="py-20 md:py-28">
          <div className="container mx-auto max-w-5xl px-5 md:px-8">
            <div className="grid gap-5 md:grid-cols-2">
              <motion.div
                {...reveal}
                transition={{ duration: 0.6 }}
                className="rounded-2xl border border-primary/25 bg-card p-7 md:p-9"
              >
                <h3 className="text-xl md:text-2xl font-semibold">C'est pour vous si...</h3>
                <ul className="mt-6 space-y-4">
                  {pourToi.map((item) => (
                    <li key={item} className="flex gap-3">
                      <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                      <span className="text-sm md:text-base leading-relaxed text-muted-foreground">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                {...reveal}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="rounded-2xl border border-border bg-secondary/30 p-7 md:p-9"
              >
                <h3 className="text-xl md:text-2xl font-semibold text-muted-foreground">
                  Ce n'est pas pour vous si...
                </h3>
                <ul className="mt-6 space-y-4">
                  {pasPourToi.map((item) => (
                    <li key={item} className="flex gap-3">
                      <X className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground/60" />
                      <span className="text-sm md:text-base leading-relaxed text-muted-foreground/80">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SECTION 7 — LE PROCESSUS */}
        <section className="py-20 md:py-28">
          <div className="container mx-auto max-w-6xl px-5 md:px-8">
            <motion.div {...reveal} transition={{ duration: 0.6 }} className="max-w-2xl">
              <Eyebrow>COMMENT ÇA MARCHE</Eyebrow>
              <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-semibold tracking-[-0.03em]">
                De la réservation à votre plan en <Accent>7 jours.</Accent>
              </h2>
            </motion.div>

            <div className="relative mt-12 md:mt-16">
              <div
                className="pointer-events-none absolute left-[19px] top-2 bottom-2 w-px md:left-0 md:right-0 md:top-[18px] md:bottom-auto md:h-px md:w-full"
                style={{
                  background:
                    "linear-gradient(to bottom, transparent, hsl(var(--primary) / 0.35), transparent)",
                }}
              />
              <div className="grid gap-8 md:grid-cols-3 md:gap-8">
                {processus.map((step, i) => (
                  <motion.div
                    key={step.num}
                    {...reveal}
                    transition={{ duration: 0.6, delay: i * 0.12 }}
                    className="relative pl-14 md:pl-0"
                  >
                    <div className="absolute left-0 top-0 grid h-10 w-10 place-items-center rounded-full border border-primary/30 bg-card md:relative md:mb-6">
                      <span style={monoStyle} className="text-xs text-primary">
                        {step.num}
                      </span>
                    </div>
                    <h3 className="text-lg md:text-xl font-semibold">{step.title}</h3>
                    <p className="mt-3 text-sm md:text-base leading-relaxed text-muted-foreground">
                      {step.body}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 8 — FAQ */}
        <section className="py-20 md:py-28">
          <div className="container mx-auto max-w-3xl px-5 md:px-8">
            <motion.h2
              {...reveal}
              transition={{ duration: 0.6 }}
              className="text-center text-3xl sm:text-4xl md:text-5xl font-semibold tracking-[-0.03em]"
            >
              Questions <Accent>fréquentes.</Accent>
            </motion.h2>

            <motion.div {...reveal} transition={{ duration: 0.6, delay: 0.1 }} className="mt-10 md:mt-14">
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((f, i) => (
                  <AccordionItem
                    key={i}
                    value={`item-${i}`}
                    className="rounded-2xl border border-border bg-card px-5 md:px-7 data-[state=open]:border-primary/30"
                  >
                    <AccordionTrigger className="py-5 text-left text-base md:text-lg font-medium hover:no-underline hover:text-primary">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 text-sm md:text-base leading-relaxed text-muted-foreground">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>

            <motion.div
              {...reveal}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-12 flex flex-col items-center text-center"
            >
              <p
                className="text-2xl md:text-3xl text-foreground"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontStyle: "italic",
                  fontSize: "1.15em",
                }}
              >
                Votre question est pas là?
              </p>
              <p className="mt-3 max-w-md text-sm md:text-base text-muted-foreground">
                Prenez 15 minutes avec moi. On répond à vos questions, et c'est tout.
                Aucune vente.
              </p>
              <Button
                variant="outline"
                className="mt-6 rounded-full border border-primary/50 bg-transparent px-7 py-5 text-sm font-medium text-primary hover:bg-primary/10"
                {...booking15MinProps}
              >
                Réserver 15 minutes
              </Button>
            </motion.div>
          </div>
        </section>

        {/* ANCRAGE DE PRIX */}
        <section className="pb-4 md:pb-8">
          <div className="container mx-auto max-w-3xl px-5 md:px-8">
            <motion.div
              {...reveal}
              transition={{ duration: 0.6 }}
              className="rounded-2xl border border-border border-l-2 border-l-primary bg-secondary/50 p-7 md:p-10"
            >
              <p
                className="text-2xl md:text-3xl text-foreground"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontStyle: "italic",
                  fontSize: "1.15em",
                }}
              >
                Mettons ça en perspective.
              </p>
              <div className="mt-5 space-y-3 text-sm md:text-base leading-relaxed text-muted-foreground">
                <p>
                  Un mois avec une agence marketing au Québec, c'est entre 3 000 et
                  6 000 dollars.
                </p>
                <p>
                  Une campagne publicitaire mal ciblée qui roule trois mois, c'est
                  facilement 5 000 dollars qui disparaissent.
                </p>
                <p>Un mois creux, c'est votre salaire.</p>
                <p className="text-foreground">
                  Le Plan de match, c'est 500 dollars. Une seule fois.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 9 — CTA FINAL */}
        <section className="relative py-24 md:py-36">
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 60% 60% at 50% 100%, hsl(var(--primary) / 0.15), transparent 70%)",
            }}
          />
          <div className="container relative mx-auto max-w-3xl px-5 md:px-8 text-center">
            <motion.h2
              {...reveal}
              transition={{ duration: 0.7 }}
              className="text-3xl sm:text-4xl md:text-6xl font-semibold tracking-[-0.03em]"
            >
              Prêt à savoir exactement <Accent>quoi faire?</Accent>
            </motion.h2>
            <motion.p
              {...reveal}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mx-auto mt-6 max-w-xl text-base md:text-lg text-muted-foreground"
            >
              Réservez votre Plan de match. Dans 7 jours, vous devinez plus : vous savez.
            </motion.p>
            <motion.p
              {...reveal}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mx-auto mt-8 max-w-2xl text-sm md:text-base font-medium text-primary"
            >
              500 dollars. Sept jours ouvrables. Et si vous trouvez que vous n'avez pas eu votre
              valeur, on vous rembourse au complet.
            </motion.p>
            <motion.div
              {...reveal}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-8 flex justify-center"
            >
              <CTAGroup />
            </motion.div>
          </div>
        </section>

        {/* Calendrier de réservation */}
        <PlanDeMatchCalendar />

      </main>


      <Footer />
      <div className="h-[72px] md:hidden" />
      <MobileStickyCTA />
    </div>
    </CTAContext.Provider>
  );
};

export default PlanDeMatchPage;
